// CONNECTORS surface (N-FDE-1 AC-5) — each Connector's connection state + health, plus registration and live
// OAuth actions. The dashboard renders `integrations`' (H) state PROJECTION (state /
// last_refresh / sanitised last_error / verified org / active scopes) and NEVER a credential value (G-140) —
// the projection type structurally carries none. A re-authorise routes an Operator intent (the Connector id
// only) to the H Gate; the freshly re-consented credential is obtained SERVER-SIDE by the owning re-consent
// flow, never through the client, and the agent Actor is refused in H (P-GATE). After a routed re-auth the
// surface re-reads the projection so it shows the state the OWNING module recorded (G-182), never an
// optimistic guess. An error Connector restarts the same OAuth begin/callback path, and a CONNECTED one
// re-consents through the same path (IFC-INTG-018) — a wider permission set is granted only by a fresh
// consent, so a live connection keeps a way back to the provider. Tokens only (G-181).

import { useCallback, useState } from 'react';
import { useSurfaceApi } from './SurfaceApiContext.tsx';
import { useProjection } from './useProjection.ts';
import { SurfaceFrame } from './surface-frame.tsx';
import { StatStrip, StatCard, SectionHead } from './primitives.tsx';
import { ConnectorList } from './components/ConnectorList.tsx';
import { ScreenToolbar, ScreenBody, TbInfo } from '../app/screen-chrome.tsx';
import type { ConnectorsProjection } from './operate-projections.ts';
import type { BeginConnectRoute, ConnectorSystemWire, RouteResult } from './api-client.ts';

/** The loaded Connectors body — the stat strip + the state/health list. */
function ConnectorsBody({
  view,
  onRegister,
  onBeginConnect,
  onReconnect,
}: {
  readonly view: ConnectorsProjection;
  readonly onRegister: (system: ConnectorSystemWire) => Promise<RouteResult>;
  readonly onBeginConnect: (connectorId: string) => Promise<BeginConnectRoute>;
  readonly onReconnect: (connectorId: string) => Promise<BeginConnectRoute>;
}): React.JSX.Element {
  const connected = view.connectors.filter((c) => c.connectionState === 'connected').length;
  const errored = view.connectors.filter((c) => c.connectionState === 'error').length;
  return (
    <div data-connectors>
      <StatStrip>
        <StatCard label="Connectors" value={view.connectors.length} note="configured" />
        <StatCard label="Connected" value={connected} note="healthy" />
        <StatCard
          label="Needs re-auth"
          value={errored}
          note="in error"
          dotTone={errored > 0 ? 'attention' : undefined}
        />
      </StatStrip>

      <section aria-label="Connector state" className="group">
        <SectionHead label="Connectors · state & health" />
        <ConnectorList
          connectors={view.connectors}
          onRegister={onRegister}
          onBeginConnect={onBeginConnect}
          onReconnect={onReconnect}
        />
      </section>
    </div>
  );
}

/**
 * Read + CONSUME the sanitised connect-outcome marker the OAuth return redirect carries (`?connect=` —
 * server.ts `connectOutcomeRedirectLocation`). Consumed once (stripped via replaceState) so a reload does
 * not re-announce a stale outcome; the value is a bounded enum, never provider detail (G-058).
 */
type ConnectOutcome = 'connected' | 'failed' | 'permissions_reset';

function consumeConnectOutcome(): ConnectOutcome | null {
  const params = new URLSearchParams(window.location.search);
  const marker = params.get('connect');
  if (marker !== 'connected' && marker !== 'failed' && marker !== 'permissions_reset') return null;
  params.delete('connect');
  const query = params.toString();
  const stripped = `${window.location.pathname}${query.length > 0 ? `?${query}` : ''}${window.location.hash}`;
  window.history.replaceState(null, '', stripped);
  return marker;
}

/**
 * The sanitised OAuth-return line (G-058): a plain outcome only; the projection beneath it is the re-read
 * truth the OWNING module recorded (G-182) — never an optimistic echo of the redirect.
 */
function ConnectOutcomeLine({
  outcome,
}: {
  readonly outcome: ConnectOutcome | null;
}): React.JSX.Element | null {
  if (outcome === null) return null;
  return (
    <p data-connect-outcome={outcome} className="mb-3 text-sm text-text">
      {outcome === 'connected'
        ? 'Connector connected.'
        : outcome === 'permissions_reset'
          ? 'Old Xero permissions cleared — click Connect once more for clean read-only access.'
          : 'The connect attempt did not complete — nothing was changed.'}
    </p>
  );
}

/** The default begin-connect navigation — a real browser hand-off (tests inject `navigate` instead). */
function assignLocation(url: string): void {
  window.location.assign(url);
}

/**
 * The two OAuth hand-offs the surface routes (split out under the function-size ceiling). Both return the
 * owning Gate's honest result and, ON OK ONLY, hand the Operator's browser to the vendor authorize page
 * (browser-mediated behind Cloudflare Access; the URL carries the anti-forgery state + public client_id only
 * — never a secret, G-058). A refusal navigates NOWHERE; the caller renders the sanitised note (P-SELF).
 */
function useOauthHandoffs(
  api: ReturnType<typeof useSurfaceApi>,
  navigate: ((url: string) => void) | undefined,
): {
  readonly onBeginConnect: (connectorId: string) => Promise<BeginConnectRoute>;
  readonly onReconnect: (connectorId: string) => Promise<BeginConnectRoute>;
} {
  const handOff = useCallback(
    async (route: Promise<BeginConnectRoute>) => {
      const result = await route;
      if (result.ok) (navigate ?? assignLocation)(result.authorizeUrl);
      return result;
    },
    [navigate],
  );
  return {
    onBeginConnect: useCallback(
      (connectorId: string) => handOff(api.routeConnectorBeginConnect(connectorId)),
      [api, handOff],
    ),
    // The RE-CONSENT of a LIVE Connector: the owning Gate retires the credential minted under the old consent
    // and mints a fresh one, so the very same browser hand-off applies.
    onReconnect: useCallback(
      (connectorId: string) => handOff(api.routeConnectorBeginReconnect(connectorId)),
      [api, handOff],
    ),
  };
}

/** The Connectors surface. `navigate` is injectable for tests; the default hands the browser to the vendor.
 *  (`role` rides the SurfaceComponent contract — rendering here is role-independent, reads are per-viewer.) */
export function ConnectorsSurface({
  navigate,
}: {
  readonly navigate?: (url: string) => void;
  readonly role?: unknown;
} = {}): React.JSX.Element {
  const api = useSurfaceApi();
  const state = useProjection<ConnectorsProjection>(api.getConnectors);
  const reload = state.reload;
  // The OAuth return outcome (if this render followed the callback redirect) — consumed exactly once.
  const [connectOutcome] = useState(consumeConnectOutcome);

  const onRegister = useCallback(
    async (system: ConnectorSystemWire) => {
      const result = await api.routeConnectorConnect(system);
      if (result.ok) reload();
      return result;
    },
    [api, reload],
  );

  const { onBeginConnect, onReconnect } = useOauthHandoffs(api, navigate);

  return (
    <>
      <ScreenToolbar title="Connectors">
        <TbInfo
          title="Connectors"
          tip="Each connector's connection state and health — never a credential value. Add, connect, and reconnect actions route Operator intents; credentials stay server-side."
        />
      </ScreenToolbar>
      <ScreenBody>
        <ConnectOutcomeLine outcome={connectOutcome} />
        <SurfaceFrame state={state}>
          {(view) => (
            <ConnectorsBody
              view={view}
              onRegister={onRegister}
              onBeginConnect={onBeginConnect}
              onReconnect={onReconnect}
            />
          )}
        </SurfaceFrame>
      </ScreenBody>
    </>
  );
}
